import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { InvoiceAPI, formatCurrency } from "../lib/api";
import { useCenturion } from "../context/CenturionContext";
import { useCompany } from "../context/CompanyContext";
import { toast } from "sonner";
import {
  Search, FileText, DollarSign, Clock, CheckCircle2,
  Eye, Pencil, Trash2, Plus, RefreshCw, Hammer, Lock,
} from "lucide-react";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "sent", label: "Sent" },
  { key: "paid", label: "Paid" },
  { key: "overdue", label: "Overdue" },
];

const DashboardPage = () => {
  const { unlocked } = useCenturion();
  const { company } = useCompany();
  const [invoices, setInvoices] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, an] = await Promise.all([
        InvoiceAPI.list({ search, status }),
        InvoiceAPI.analytics(),
      ]);
      setInvoices(list);
      setAnalytics(an);
    } catch (e) {
      toast.error("Failed to load invoices");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable react-hooks/exhaustive-deps */ }, [search, status]);

  const handleDelete = async (id) => {
    try {
      await InvoiceAPI.remove(id);
      toast.success("Invoice deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const sortedByDate = useMemo(
    () => (Array.isArray(invoices) ? [...invoices] : []).sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    [invoices]
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] mb-2">Overview</div>
            <h1 className="font-display text-[40px] md:text-[52px] font-semibold leading-[1.05] tracking-tight">
              Invoice Dashboard
            </h1>
            <p className="text-white/55 text-[14px] mt-2 max-w-xl">
              Every invoice forged by {company.company_name} — search, filter, verify, and export to PDF in one click.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-ghost" disabled={loading}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <Link to="/studio/invoice" className="btn-ghost">
              <Hammer size={14} /> Forge Studio
            </Link>
            <Link to="/documents" className="btn-ghost">
              <FileText size={14} /> Documents
            </Link>
            {unlocked && (
              <Link to="/invoices/new" className="btn-primary">
                <Plus size={14} /> NEW INVOICE
              </Link>
            )}
          </div>
        </div>

        {/* Analytics cards */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard
            icon={<FileText size={16} />}
            label="Total Invoices"
            value={analytics ? analytics.count : "—"}
            sub={analytics ? `${analytics.by_status?.sent || 0} active` : ""}
          />
          <StatCard
            icon={<DollarSign size={16} />}
            label="Total Billed"
            value={analytics ? formatCurrency(analytics.total_billed) : "—"}
            sub="Across all invoices"
            accent
          />
          <StatCard
            icon={<CheckCircle2 size={16} />}
            label="Paid"
            value={analytics ? formatCurrency(analytics.total_paid) : "—"}
            sub={`${analytics?.by_status?.paid || 0} invoices`}
          />
          <StatCard
            icon={<Clock size={16} />}
            label="Outstanding"
            value={analytics ? formatCurrency(analytics.total_outstanding) : "—"}
            sub="Sent + Overdue"
          />
        </section>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by invoice #, project, or client…"
              className="field pl-10"
            />
          </div>
          <div className="flex items-center gap-1 bg-white/4 border border-white/8 rounded-md p-1 overflow-x-auto">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setStatus(t.key)}
                className={`px-3 py-1.5 text-[12px] rounded transition-colors whitespace-nowrap ${status === t.key ? "bg-[#d4a54b] text-black font-semibold" : "text-white/60 hover:text-white"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Invoice list */}
        {loading ? (
          <div className="grid gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="glass h-[88px] animate-pulse opacity-60" />
            ))}
          </div>
        ) : sortedByDate.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid gap-3">
            {sortedByDate.map((inv) => (
              <InvoiceRow
                key={inv.id}
                invoice={inv}
                onAskDelete={() => setConfirmDelete(inv)}
              />
            ))}
          </div>
        )}

        {/* Top clients */}
        {analytics?.top_clients?.length > 0 && (
          <section className="mt-12">
            <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] mb-4">Top Clients</h3>
            <div className="glass p-4">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-white/50 text-[11px] uppercase tracking-wider">
                    <th className="py-2 font-medium">Client</th>
                    <th className="py-2 font-medium text-right">Total Billed</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_clients.map((c) => (
                    <tr key={c.client} className="border-t border-white/5">
                      <td className="py-3">{c.client}</td>
                      <td className="py-3 text-right font-mono text-[#d4a54b]">{formatCurrency(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="glass max-w-md w-full mx-4 p-6">
            <h3 className="text-[18px] font-semibold mb-2">Delete invoice?</h3>
            <p className="text-white/60 text-[13px] leading-relaxed">
              You're about to delete <span className="text-white font-mono">{confirmDelete.invoice_number}</span>. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button className="btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => handleDelete(confirmDelete.id)}>
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const StatCard = ({ icon, label, value, sub, accent }) => (
  <div className="glass p-5">
    <div className="flex items-center gap-2 text-white/55 text-[11px] uppercase tracking-[0.12em]">
      <span className={accent ? "text-[#d4a54b]" : ""}>{icon}</span> {label}
    </div>
    <div className={`mt-3 text-[26px] font-semibold tracking-tight ${accent ? "text-[#e0bc62]" : ""}`}>
      {value}
    </div>
    {sub && <div className="text-[11px] text-white/40 mt-1">{sub}</div>}
  </div>
);

const InvoiceRow = ({ invoice, onAskDelete }) => {
  const { unlocked } = useCenturion();
  return (
    <div className="glass interactive p-5 flex flex-col md:flex-row md:items-center gap-4 group">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <span className="font-mono text-[11px] text-[#d4a54b] tracking-wider">#{invoice.invoice_number}</span>
          <span className={`status-badge ${invoice.status}`}>{invoice.status}</span>
          {invoice.stack_report && (
            <span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.1em] bg-[rgba(212,165,75,0.08)] text-[#d4a54b]/80 border border-[rgba(212,165,75,0.18)] flex items-center gap-1">
              <Hammer size={9} /> Forged
            </span>
          )}
        </div>
        <h3 className="text-[16px] font-medium leading-snug truncate">
          {invoice.title || invoice.project_name}
        </h3>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[12px] text-white/55">
          <span>To: <span className="text-white/80">{invoice.to_party?.company}</span></span>
          <span>•</span>
          <span>{invoice.invoice_date}</span>
          {invoice.due_date && <><span>•</span><span>Due: {invoice.due_date}</span></>}
        </div>
      </div>

      <div className="md:text-right">
        <div className="text-[10px] uppercase tracking-[0.16em] text-white/45">Total</div>
        <div className="text-[22px] font-semibold tracking-tight font-mono text-[#e0bc62]">
          {formatCurrency(invoice.total, invoice.currency)}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Link
          to={`/invoices/${invoice.id}`}
          className="px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[12px] flex items-center gap-1.5 transition-colors"
        >
          <Eye size={14} /> View
        </Link>
        {unlocked ? (
          <>
            <Link
              to={`/invoices/${invoice.id}/edit`}
              className="px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[12px] flex items-center gap-1.5 transition-colors"
            >
              <Pencil size={14} /> Edit
            </Link>
            <button
              onClick={onAskDelete}
              className="px-2.5 py-2 rounded-md bg-white/5 hover:bg-red-500/10 border border-white/10 hover:border-red-500/40 text-[12px] flex items-center gap-1.5 text-white/70 hover:text-red-400 transition-colors"
              aria-label="Delete invoice"
            >
              <Trash2 size={14} />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

const EmptyState = () => {
  const { unlocked } = useCenturion();
  return (
    <div className="glass p-14 text-center">
      <div className="w-14 h-14 rounded-full bg-[rgba(212,165,75,0.08)] mx-auto flex items-center justify-center text-[#d4a54b] mb-5">
        <Hammer size={24} />
      </div>
      <h3 className="font-display text-[28px] font-semibold">No invoices yet</h3>
      <p className="text-white/60 text-[13px] mt-2 max-w-sm mx-auto">
        Drop a project zip into Forge Studio to generate your first auditable invoice.
      </p>
      <div className="flex items-center justify-center gap-3 mt-6">
        <Link to="/studio/invoice" className="btn-primary">
          <Hammer size={14} /> Open Forge Studio
        </Link>
        {unlocked && (
          <Link to="/invoices/new" className="btn-ghost">
            <Plus size={14} /> Manual Invoice
          </Link>
        )}
      </div>
      {!unlocked && (
        <p className="mt-6 text-[11px] text-white/35 flex items-center justify-center gap-1.5">
          <Lock size={11} /> Issuer mode locked — type the keyword to unlock manual creation.
        </p>
      )}
    </div>
  );
};

export default DashboardPage;
